import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CalendarDots, ListBullets, CaretLeft, CaretRight } from "@phosphor-icons/react";

const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

export default function Releases() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("calendar");
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/releases");
      setItems(data);
      setLoading(false);
    })();
  }, []);

  const grouped = items.reduce((acc, r) => {
    const d = new Date(r.release_date);
    const key = d.toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
    acc[key] = acc[key] || [];
    acc[key].push(r);
    return acc;
  }, {});

  return (
    <div className="pt-20 max-w-6xl mx-auto px-5 lg:px-8 pb-24" data-testid="releases-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="font-mono-accent text-xs text-[#E63946] mb-2">◢ CALENDÁRIO</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">Próximos Lançamentos</h1>
          <p className="text-zinc-400 mt-2">O que vem aí nas estantes portuguesas.</p>
        </div>
        <div className="inline-flex rounded-lg border border-white/10 bg-[#121217] p-1 self-start" data-testid="releases-view-toggle">
          <button onClick={() => setView("calendar")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${view === "calendar" ? "bg-[#E63946] text-white" : "text-zinc-400 hover:text-white"}`} data-testid="releases-view-calendar">
            <CalendarDots size={16} weight="bold" /> Calendário
          </button>
          <button onClick={() => setView("list")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${view === "list" ? "bg-[#E63946] text-white" : "text-zinc-400 hover:text-white"}`} data-testid="releases-view-list">
            <ListBullets size={16} weight="bold" /> Lista
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center font-mono-accent text-xs text-zinc-500 py-10">A CARREGAR...</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl py-20 text-center">
          <CalendarDots size={42} weight="duotone" className="text-[#7209B7] mx-auto mb-3" />
          <div className="font-display font-bold text-xl">Sem lançamentos agendados</div>
        </div>
      ) : view === "calendar" ? (
        <CalendarView items={items} cursor={cursor} setCursor={setCursor} />
      ) : (
        <div className="space-y-10">
          {Object.entries(grouped).map(([month, list]) => (
            <div key={month}>
              <div className="font-mono-accent text-xs text-[#E63946] mb-4 uppercase">{month}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {list.map((r) => (
                  <Link key={r.release_id} to={`/lancamentos/${r.release_id}`} className="card-hover bg-[#121217] border border-white/5 rounded-xl overflow-hidden block group" data-testid={`release-${r.release_id}`}>
                    {r.cover_url ? (
                      <div className="aspect-[2/3] overflow-hidden"><img src={r.cover_url} alt={r.title} className="manga-cover w-full h-full object-cover" /></div>
                    ) : <div className="aspect-[2/3] bg-gradient-to-br from-[#7209B7]/20 to-[#E63946]/20" />}
                    <div className="p-4">
                      <div className="font-mono-accent text-[10px] text-[#E63946]">{new Date(r.release_date).toLocaleDateString("pt-PT", { day: "2-digit", month: "short" })}</div>
                      <div className="font-display font-bold leading-tight mt-1 line-clamp-2 group-hover:text-[#E63946] transition-colors">{r.title}</div>
                      {r.publisher && <div className="text-xs text-zinc-500 mt-1">{r.publisher}</div>}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CalendarView({ items, cursor, setCursor }) {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();

  const byDay = useMemo(() => {
    const map = {};
    items.forEach((r) => {
      const d = new Date(r.release_date);
      if (d.getFullYear() === year && d.getMonth() === month) {
        const day = d.getDate();
        map[day] = map[day] || [];
        map[day].push(r);
      }
    });
    return map;
  }, [items, year, month]);

  // Build grid — weeks start on Monday
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Mon=0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthLabel = cursor.toLocaleDateString("pt-PT", { month: "long", year: "numeric" });
  const today = new Date();
  const isToday = (d) => today.getFullYear() === year && today.getMonth() === month && today.getDate() === d;

  return (
    <div data-testid="releases-calendar">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="w-9 h-9 rounded-lg border border-white/10 grid place-items-center text-zinc-300 hover:bg-white/5" data-testid="cal-prev"><CaretLeft size={16} weight="bold" /></button>
        <div className="font-display text-xl font-bold capitalize" data-testid="cal-month-label">{monthLabel}</div>
        <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="w-9 h-9 rounded-lg border border-white/10 grid place-items-center text-zinc-300 hover:bg-white/5" data-testid="cal-next"><CaretRight size={16} weight="bold" /></button>
      </div>
      <div className="grid grid-cols-7 gap-1.5 mb-1.5">
        {WEEKDAYS.map((w) => <div key={w} className="text-center font-mono-accent text-[10px] text-zinc-500 py-1">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {cells.map((d, i) => (
          <div key={i} className={`min-h-[92px] rounded-lg border p-1.5 ${d ? "border-white/5 bg-[#0f0f14]" : "border-transparent"} ${isToday(d) ? "ring-1 ring-[#E63946]/60" : ""}`}>
            {d && (
              <>
                <div className={`text-[11px] font-mono-accent mb-1 ${isToday(d) ? "text-[#E63946]" : "text-zinc-500"}`}>{d}</div>
                <div className="space-y-1">
                  {(byDay[d] || []).slice(0, 3).map((r) => (
                    <Link key={r.release_id} to={`/lancamentos/${r.release_id}`} className="block text-[10px] leading-tight px-1.5 py-1 rounded bg-[#E63946]/15 text-[#ff9aa3] hover:bg-[#E63946]/25 truncate" title={r.title} data-testid={`cal-release-${r.release_id}`}>
                      {r.title}
                    </Link>
                  ))}
                  {(byDay[d] || []).length > 3 && <div className="text-[9px] text-zinc-500 px-1">+{byDay[d].length - 3} mais</div>}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
